import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";
import { WORKSHOPS } from "@/content/workshops";
import {
  FINDING_DOMAINS,
  FINDING_DOMAIN_DEFAULT,
  FINDING_SEVERITIES,
  FINDING_SEVERITY_DEFAULT,
  FINDING_STATUSES,
  FINDING_STATUS_DEFAULT,
  type FindingDomain,
  type FindingSeverity,
  type FindingStatus,
} from "@/lib/taxonomy";

/**
 * Findings register (Track A · P0.2 skeleton → REG-FIND body). A finding is
 * an observation/insight surfaced during a workshop: a risk, a gap, a
 * pattern worth recording. The unified store the three source plans each
 * named differently (plan2 *Findings* == plan3 `useFindings` == plan1
 * *Insights*) — **one** store, replacing the former static `F-01..F-07`
 * sample rows. Built on {@link createWorkshopRecordStore}; persistence is
 * IndexedDB by default (the BFF blob plane when `VITE_WORKSHOP_BACKEND=bff`).
 *
 * `linkedWorkshopIds` records which *other* workshops a finding spans (a
 * cross-workshop theme); the sanitiser drops any id that isn't a real
 * workshop so a corrupt blob can't smuggle in a phantom scope.
 */

export type { FindingSeverity, FindingStatus, FindingDomain };

export type Finding = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  workshopId: string;
  title: string;
  /** Free-text body — the observation itself. */
  detail: string;
  severity: FindingSeverity;
  status: FindingStatus;
  domain: FindingDomain;
  /** Other workshops this finding also bears on (cross-workshop themes).
   *  Always a real workshop id; filtered to known ids on sanitise. */
  linkedWorkshopIds: string[];
  /** Who owns following it up (display name). */
  owner: string;
  createdAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 seam). Optional; absent on legacy records. */
  tags?: string[];
};

/** Mutable fields a facilitator can edit after a finding is logged. */
export type FindingPatch = Partial<
  Pick<
    Finding,
    | "title"
    | "detail"
    | "severity"
    | "status"
    | "domain"
    | "linkedWorkshopIds"
    | "owner"
    | "tags"
  >
>;

const KNOWN_WORKSHOP_IDS = new Set<string>(WORKSHOPS.map((w) => w.id));

export const useFindings = createWorkshopRecordStore<Finding, FindingPatch>({
  idbKey: "xaes-findings",
  recordType: "findings",
  // Best-effort sanitiser: drop rows missing an id/title; coerce unknown
  // enum values to their neutral default; filter linkedWorkshopIds to real
  // workshops. Mirrors the shared sanitiser rationale in useDecisions.
  sanitiseItem: (f, fallbackWorkshopId) => {
    if (typeof f.id !== "string") return null;
    if (typeof f.title !== "string") return null;
    const severity: FindingSeverity = FINDING_SEVERITIES.includes(
      f.severity as FindingSeverity,
    )
      ? (f.severity as FindingSeverity)
      : FINDING_SEVERITY_DEFAULT;
    const status: FindingStatus = FINDING_STATUSES.includes(
      f.status as FindingStatus,
    )
      ? (f.status as FindingStatus)
      : FINDING_STATUS_DEFAULT;
    const domain: FindingDomain = FINDING_DOMAINS.includes(
      f.domain as FindingDomain,
    )
      ? (f.domain as FindingDomain)
      : FINDING_DOMAIN_DEFAULT;
    return {
      id: f.id,
      workshopId:
        typeof f.workshopId === "string" ? f.workshopId : fallbackWorkshopId,
      title: f.title,
      detail: typeof f.detail === "string" ? f.detail : "",
      severity,
      status,
      domain,
      linkedWorkshopIds: Array.isArray(f.linkedWorkshopIds)
        ? f.linkedWorkshopIds.filter(
            (id): id is string =>
              typeof id === "string" && KNOWN_WORKSHOP_IDS.has(id),
          )
        : [],
      owner: typeof f.owner === "string" ? f.owner : "",
      createdAt: typeof f.createdAt === "number" ? f.createdAt : Date.now(),
      addedBy: typeof f.addedBy === "string" ? f.addedBy : "",
      tags: Array.isArray(f.tags)
        ? f.tags.filter((t): t is string => typeof t === "string")
        : undefined,
    };
  },
});
